import React from 'react'
import { inject, observer } from 'mobx-react'
import { Input, Button, Checkbox, message } from 'antd'
import { ILocation, IPayment } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../utilities/helpers'
import { Receipt } from '../../../components/Receipt/Receipt'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { samplePaymentForReceipt } from './receiptHelpers'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  selectedLocationId?: number
}

interface State {
  isSaving: boolean
  errorMessage?: string
  editLocation?: ILocation
  name: string
  address: string
  timezone: string
  description?: string
  receiptLocationNameText: string | null
  receiptLocationDescriptionText: string | null
  receiptAddressText: string | null
  receiptFooterText: string | null
  receiptShowEmployee: boolean
  samplePayment: IPayment
  receiptShowSku: boolean
}

@inject('currentCompanyStore')
@observer
export class ReceiptFormV1 extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      isSaving: false,
      name: '',
      address: '',
      description: '',
      timezone: '',
      receiptLocationNameText: null,
      receiptLocationDescriptionText: null,
      receiptAddressText: null,
      receiptFooterText: null,
      receiptShowEmployee: true,
      receiptShowSku: false,
      samplePayment: samplePaymentForReceipt,
    }
  }

  componentDidMount() {
    this.observeSelectedLoaction(this.props.selectedLocationId)
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.selectedLocationId !== this.props.selectedLocationId) {
      this.observeSelectedLoaction(nextProps.selectedLocationId)
    }
  }

  observeSelectedLoaction = (selectedLocationId?: number) => {
    const locations = this.props.currentCompanyStore!.locations || []
    const matchingLocation = locations.find((location) => location.id === selectedLocationId)

    if (matchingLocation) {
      this.setState({
        editLocation: matchingLocation,
        name: matchingLocation.name,
        description: matchingLocation.description,
        address: matchingLocation.address,
        timezone: matchingLocation.timezone,
        receiptLocationNameText: matchingLocation.receiptSettings.receiptLocationNameText,
        receiptLocationDescriptionText:
          matchingLocation.receiptSettings.receiptLocationDescriptionText,
        receiptAddressText: matchingLocation.receiptSettings.receiptAddressText,
        receiptFooterText: matchingLocation.receiptSettings.receiptFooterText,
        receiptShowEmployee: matchingLocation.receiptSettings.receiptShowEmployee,
        receiptShowSku: matchingLocation.receiptSettings.receiptShowSku,
      })
    }
  }

  editLocation = async () => {
    this.setState({ isSaving: true })

    try {
      await this.props.currentCompanyStore!.editLocation({
        id: this.state.editLocation!.id,
        name: this.state.name,
        address: this.state.address,
        description: this.state.description || '',
        timezone: this.state.timezone,
        leaflyApiKey: this.state.editLocation!.leaflyApiKey,
        receiptSettings: {
          receiptLocationNameText: this.state.receiptLocationNameText || null,
          receiptLocationDescriptionText: this.state.receiptLocationDescriptionText || null,
          receiptAddressText: this.state.receiptAddressText || null,
          receiptFooterText: this.state.receiptFooterText || null,
          receiptShowEmployee: this.state.receiptShowEmployee,
          receiptShowSku: this.state.receiptShowSku,
        },
        companyId: this.company.id,
      })

      await this.props.currentCompanyStore!.getLocations()
      message.success(`Receipt for ${this.state.name} was successfully saved`, 1)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while editing location')
    } finally {
      this.setState({ isSaving: false })
    }
  }

  render() {
    const {
      isSaving,
      samplePayment,
      receiptLocationNameText,
      receiptLocationDescriptionText,
      receiptAddressText,
      receiptFooterText,
      receiptShowEmployee,
      receiptShowSku,
    } = this.state

    const combinedPayment = {
      ...samplePayment,
      receiptSettings: {
        receiptLocationNameText,
        receiptLocationDescriptionText,
        receiptAddressText,
        receiptFooterText,
        receiptShowEmployee,
        receiptShowSku,
      },
    }

    return (
      <div className='row mt-4'>
        <section className='col-sm-4 mb-4'>
          <div className='form-group'>
            <label>Location name</label>
            <Input
              value={receiptLocationNameText || ''}
              onChange={(e) => this.setState({ receiptLocationNameText: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <label>Location description</label>
            <Input
              value={receiptLocationDescriptionText || ''}
              onChange={(e) => this.setState({ receiptLocationDescriptionText: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <label>Location address</label>
            <Input
              value={receiptAddressText || ''}
              onChange={(e) => this.setState({ receiptAddressText: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <label>Footer text</label>
            <Input.TextArea
              rows={3}
              value={receiptFooterText || ''}
              onChange={(e) => this.setState({ receiptFooterText: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <Checkbox
              checked={receiptShowEmployee}
              onChange={() => this.setState({ receiptShowEmployee: !receiptShowEmployee })}
            >
              Show employee name
            </Checkbox>
          </div>

          <div className='form-group'>
            <Checkbox
              checked={receiptShowSku}
              onChange={() => this.setState({ receiptShowSku: !receiptShowSku })}
            >
              Show SKUs
            </Checkbox>
          </div>

          <Button type='primary' onClick={this.editLocation} loading={isSaving}>
            Save
          </Button>
        </section>

        <section className='col-sm-6'>
          <div className='ml-4 mr-4 p-2 background-shadow' style={{ maxWidth: '20rem' }}>
            <Receipt company={this.company} payment={combinedPayment} />
          </div>
        </section>
      </div>
    )
  }
}
